import {
  CommandListStrategy,
  CommandObject,
  CommandResponse,
  ProcessResponseArg
} from './commandListStrategy';
import {
  ITreeFolder,
  ITreeFile,
  ITreeObject,
  getFilePath
} from './../../../../codeGeneration/codeGen';
import { useControlCenterStore } from '../../store';
import { BaseWSErrorMsg } from './robotConnection';

export interface BuildWorkspaceResponseArg extends ProcessResponseArg {
  extraInfo?: {
    objects: Array<ITreeObject>;
    commandTarget: ITreeObject;
  };
}

export class BuildWorkspaceOnRobotStrategy extends CommandListStrategy {
  targetDirectory: string;
  fileTree: ITreeObject;
  setErrorText: (text: string) => void = () => {};
  packageNameList: Array<string>;
  constructor(
    extraCommands: string,
    targetDirectory: string,
    fileTree: ITreeFolder,
    packageNameList: Array<string> = []
  ) {
    super(extraCommands);
    this.targetDirectory = targetDirectory;
    this.fileTree = fileTree;
    this.packageNameList = packageNameList;
    while (this.targetDirectory.endsWith('/')) {
      this.targetDirectory = this.targetDirectory.slice(
        this.targetDirectory.lastIndexOf('/')
      );
    }

    this.commandList = [
      {
        command: `mkdir -p ${this.targetDirectory}/src`,
        processResponse: this.processTreeFile,
        extraInfo: {
          objects: [this.fileTree]
        }
      }
    ];
  }
  //TODO: include option for if directories already exist
  processTreeFile(args: BuildWorkspaceResponseArg): CommandResponse {
    if (
      args.response &&
      args.response.includes('mkdir: cannot create directory')
    ) {
      return CommandResponse.FAILURE;
    }
    if (!args.extraInfo || !args.extraInfo.objects)
      return CommandResponse.SUCCESS;
    const strategy = args.strategy as BuildWorkspaceOnRobotStrategy;
    let commandsToAdd: CommandObject[] = [];
    let responseLoadingString = 'Building packages on robot';
    for (let obj of args.extraInfo.objects) {
      if (obj.type !== 'folder' && obj.type !== 'file') continue;
      const filePath = `${strategy.targetDirectory}/src/${getFilePath(
        strategy.fileTree,
        obj
      )}`;
      if (obj.type === 'folder') {
        responseLoadingString = `Building folder ${obj.name}`;
        commandsToAdd.push({
          command: `rm -r ${filePath} 2>/dev/null; mkdir ${filePath}`,
          processResponse: strategy.processTreeFile,
          extraInfo: {
            objects: (obj as ITreeFolder).children || [],
            commandTarget: obj
          }
        });
      }
      // create and write to file, replace all " with \" for use with echo
      const file = obj as ITreeFile;
      responseLoadingString = `Building file ${file.name}`;
      if (file.data && file.data.trim().length !== 0) {
        // formatted to add escapes for special echo characters
        const formattedFileData = file.data
          .replaceAll('"', '\\"')
          .replaceAll('$', '\\$');
        commandsToAdd.push({
          command: `rm ${filePath} 2>/dev/null; touch ${filePath} 2>/dev/null; echo "${formattedFileData}" > ${filePath}`,
          processResponse: strategy.processTreeFile,
          extraInfo: {
            objects: [],
            commandTarget: file
          }
        });
      }
      // if file empty just create
      else
        commandsToAdd.push({
          command: `rm ${filePath} 2>/dev/null; touch ${filePath} 2>/dev/null;`,
          processResponse: strategy.processTreeFile,
          extraInfo: {
            objects: [],
            commandTarget: file
          }
        });
    }
    strategy.loadProgressFunc(
      responseLoadingString,
      (100 * strategy.currIndex) / strategy.commandList.length
    );
    if (
      commandsToAdd.length === 0 &&
      strategy.currIndex === strategy.commandList.length - 1
    ) {
      let pkgSelectText = `--packages-select ${strategy.packageNameList.join(
        ' '
      )}`;
      commandsToAdd.push({
        command: `cd ${
          strategy.targetDirectory
        }; colcon build --symlink-install ${
          strategy.packageNameList.length > 0 ? pkgSelectText : ''
        }`,
        processResponse: strategy.processBuild,
        extraInfo: {
          objects: []
        }
      });
    }
    strategy.addCommands(commandsToAdd);

    return CommandResponse.SUCCESS;
  }

  processBuild(args: BuildWorkspaceResponseArg): CommandResponse {
    if (!args.response) {
      return CommandResponse.FAILURE;
    }

    args.strategy.loadProgressFunc('Finished Building Packages', 99);
    setTimeout(
      () =>
        args.strategy.loadProgressFunc('Finished Building Packages', 100, true),
      1000
    );
    return CommandResponse.SUCCESS;
  }

  finishCreation(): void {}

  processErrorResponse(error: BaseWSErrorMsg): void {
    if (!error.isError) return;
    const currMessage = this.getCurrentCommand();
    if (!currMessage)
      this.setErrorText('Error: Failed to build packages on the robot');
    else if (currMessage.command.includes('colcon build'))
      this.setErrorText(`Error: colcon build failed: ${error.errorMsg}`);
    else if (currMessage.extraInfo.commandTarget) {
      const target = currMessage.extraInfo.commandTarget;
      if (target.type && target.type === 'folder') {
        this.setErrorText(
          `Error: failed to build folder ${target.name}: ${error.errorMsg}`
        );
      } else if (target.type && target.type === 'file') {
        this.setErrorText(
          `Error: failed to build file ${target.name}: ${error.errorMsg}`
        );
      }
    } else {
      this.setErrorText(
        `Error: failed to run command ${currMessage.command}: error response: ${error.errorMsg}`
      );
    }
  }
}
